import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import 'boxicons/css/boxicons.min.css';
import { useAuth } from '../../Constant/Auth';
import * as Constants from '../../Constant/constant';

import {
  getUserDataById,
  getErrorMessage
} from '../../api/api';

const ParentProfile = () => {
  const { user } = useAuth();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imgError, setImgError] = useState(false);

  const fetchProfile = async () => {
    try {
      if (!user?.id) return;

      setLoading(true);
      const data = await getUserDataById(user.id);
      setProfile(data || user);
    } catch (error) {
      console.error('Parent profile error:', error);
      Swal.fire('Error', getErrorMessage(error), 'error');
      setProfile(user);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const getImageUrl = (path) => {
    if (!path) return null;
    if (String(path).startsWith('http')) return path;

    const cleanPath = String(path).replace(/\\/g, '/').replace(/^\/+/, '');
    return `${Constants.IMAGE_BASE_URL}/${cleanPath}`;
  };

  if (loading || !profile) {
    return (
      <div className="container-fluid mt-3">
        <div className="text-center py-5">Loading...</div>
      </div>
    );
  }

  const name =
    profile.first_name && profile.last_name
      ? `${profile.first_name} ${profile.last_name}`
      : profile.full_name || profile.name || profile.parent_name || 'Parent';

  const img = profile.user_img || profile.image || profile.profile_image;

  const isActive = Number(profile.status) !== 0;
  const initials =
    (String(name).match(/\b\w/g) || []).slice(0, 2).join('').toUpperCase() || 'U';
  const showImg = img && !imgError;

  // Color-coded detail fields (blue family, matched to the sidebar / dashboard)
  const details = [
    { label: 'Unique ID', value: profile.unique_id || '-', icon: 'bx bx-id-card', c: '#2563eb' },
    { label: 'Full Name', value: name, icon: 'bx bx-user', c: '#6d28d9' },
    { label: 'Email', value: profile.email || '-', icon: 'bx bx-envelope', c: '#0369a1' },
    { label: 'Mobile', value: profile.mobile_no || profile.mobile || '-', icon: 'bx bx-phone', c: '#0f766e' }
    // { label: 'Role ID', value: profile.role_id || '-', icon: 'bx bx-shield-quarter', c: '#4338ca' },
    // { label: 'Library ID', value: profile.library_id || '-', icon: 'bx bx-buildings', c: '#0891b2' }
  ];

  return (
    <div className="container-fluid mt-3 pp-page">
      <style>{`
        .pp-page { animation: ppFade .5s ease both; padding: 4px 16px 26px 24px; }

        /* reserve space on the right so hero text never sits under the illustration */
        @media (min-width: 992px) { .pp-hero { padding-right: 272px; } }

        /* ── Hero header (blue, matched to sidebar) ── */
        .pp-hero {
          position: relative; overflow: hidden;
          border-radius: 24px; padding: 36px 34px;
          min-height: 230px;
          display: flex; align-items: center; gap: 22px;
          background: linear-gradient(135deg, #1e3a8a 0%, #2563eb 55%, #3b82f6 120%);
          color: #fff; box-shadow: 0 18px 42px rgba(30, 58, 138, .32);
          animation: ppUp .5s ease both;
        }
        .pp-hero::before {
          content: ""; position: absolute; right: 90px; top: -60px;
          width: 150px; height: 150px; border-radius: 50%; background: rgba(255,255,255,.08);
        }
        .pp-hero::after {
          content: ""; position: absolute; right: -60px; bottom: -80px;
          width: 220px; height: 220px; border-radius: 50%; background: rgba(255,255,255,.12);
        }
        .pp-avatar {
          width: 104px; height: 104px; border-radius: 50%; flex-shrink: 0; z-index: 1;
          overflow: hidden; display: flex; align-items: center; justify-content: center;
          background: rgba(255,255,255,.18); color: #fff;
          font-size: 38px; font-weight: 800; letter-spacing: 1px;
          border: 4px solid rgba(255,255,255,.7);
          box-shadow: 0 10px 26px rgba(2, 28, 55, .36);
          animation: ppPop .6s cubic-bezier(.18,.89,.32,1.28) both;
        }
        .pp-avatar img { width: 100%; height: 100%; object-fit: cover; }
        .pp-hero-info { z-index: 1; min-width: 0; }

        /* ── Animated reading scene (same as dashboard) ── */
        .pp-hero-art {
          position: absolute; right: 26px; bottom: 0; z-index: 0;
          width: 240px; height: auto; pointer-events: none;
          animation: artFloat 5s ease-in-out infinite;
        }
        .pp-hero-art .art-girl { animation: artFloat 4s ease-in-out infinite; transform-origin: bottom center; }
        .pp-hero-art .art-head { animation: artNod 3.6s ease-in-out infinite; transform-origin: 86px 102px; }
        .pp-hero-art .art-book-float { animation: artBook 3.4s ease-in-out infinite; transform-origin: 138px 28px; }
        .pp-hero-art .art-page { animation: artPage 2.8s ease-in-out infinite; transform-origin: 86px 138px; }
        .pp-hero-art .art-plant { animation: artSway 4.5s ease-in-out infinite; transform-origin: 150px 164px; }
        .pp-hero-art .art-twinkle { animation: artTwinkle 2.4s ease-in-out infinite; }
        .pp-hero-art .art-twinkle.t2 { animation-delay: .6s; }
        .pp-hero-art .art-twinkle.t3 { animation-delay: 1.2s; }

        @keyframes artFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
        @keyframes artNod   { 0%,100% { transform: rotate(0deg); } 50% { transform: rotate(-4deg); } }
        @keyframes artSway  { 0%,100% { transform: rotate(-2deg); } 50% { transform: rotate(2deg); } }
        @keyframes artBook  { 0%,100% { transform: translateY(0) rotate(-4deg); } 50% { transform: translateY(-7px) rotate(4deg); } }
        @keyframes artPage  { 0%,70%,100% { transform: scaleX(1); } 35% { transform: scaleX(.05); } }
        @keyframes artTwinkle { 0%,100% { opacity: .2; transform: scale(.7); } 50% { opacity: 1; transform: scale(1); } }
        @media (max-width: 991px) { .pp-hero-art { display: none; } }
        .pp-eyebrow { font-size: 11.5px; letter-spacing: .16em; text-transform: uppercase; color: #bfdbfe; font-weight: 700; }
        .pp-hero h2 { color: #fff; font-weight: 800; margin: 4px 0 2px; letter-spacing: -.5px; font-size: 1.8rem; }
        .pp-email { color: rgba(255,255,255,.9); font-size: 13.5px; display: flex; align-items: center; gap: 6px; }
        .pp-chips { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
        .pp-status, .pp-chip {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 5px 13px; border-radius: 999px; font-size: 12px; font-weight: 700;
        }
        .pp-status { background: rgba(255,255,255,.95); color: ${isActive ? '#15803d' : '#b45309'}; }
        .pp-status .dot { width: 8px; height: 8px; border-radius: 50%; background: ${isActive ? '#22c55e' : '#f59e0b'}; }
        .pp-chip {
          background: rgba(255,255,255,.16); border: 1px solid rgba(191,219,254,.45);
          color: #eff6ff; backdrop-filter: blur(4px);
        }
        .pp-chip i { font-size: 14px; }

        /* ── Section heading ── */
        .pp-section { display: flex; align-items: center; gap: 8px; margin: 22px 2px 14px; }
        .pp-section i { color: #2563eb; font-size: 20px; }
        .pp-section h4 { font-weight: 800; color: #1e3a8a; margin: 0; font-size: 1.15rem; }

        /* ── Detail grid ── */
        .pp-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 16px; }
        @media (max-width: 992px) { .pp-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 576px) { .pp-grid { grid-template-columns: 1fr; } }

        .pp-item {
          position: relative; overflow: hidden;
          display: flex; align-items: center; gap: 14px; text-align: left;
          background: #fff; border: 1px solid #eef1f8;
          border-radius: 16px; padding: 16px 18px;
          box-shadow: 0 8px 22px rgba(16, 16, 39, .06);
          transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease;
          animation: ppUp .5s ease both;
        }
        .pp-item::before { content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 4px; background: var(--c); }
        .pp-item:hover {
          transform: translateY(-4px);
          box-shadow: 0 16px 32px color-mix(in srgb, var(--c) 20%, transparent);
          border-color: color-mix(in srgb, var(--c) 32%, #eef1f8);
        }
        .pp-ico {
          width: 48px; height: 48px; border-radius: 14px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          font-size: 23px; color: #fff;
          background: linear-gradient(135deg, var(--c), color-mix(in srgb, var(--c) 70%, #000 12%));
          box-shadow: 0 8px 16px color-mix(in srgb, var(--c) 34%, transparent);
          transition: transform .2s ease;
        }
        .pp-item:hover .pp-ico { transform: scale(1.06) rotate(-3deg); }
        .pp-label { font-size: 11.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #94a3b8; }
        .pp-value { font-size: 15.5px; font-weight: 800; color: #1e293b; word-break: break-word; }

        .pp-note {
          margin-top: 18px; border-radius: 16px; padding: 14px 18px;
          background: linear-gradient(135deg, #eff6ff, #f5f3ff);
          border: 1px solid #dbeafe; color: #1d4ed8; font-weight: 600;
          display: flex; align-items: center; gap: 10px;
          animation: ppUp .5s ease both; animation-delay: .4s;
        }

        .pp-d1{animation-delay:.06s}.pp-d2{animation-delay:.1s}.pp-d3{animation-delay:.14s}
        .pp-d4{animation-delay:.18s}.pp-d5{animation-delay:.22s}.pp-d6{animation-delay:.26s}.pp-d7{animation-delay:.3s}

        @keyframes ppFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes ppUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes ppPop { from { opacity: 0; transform: scale(.6); } to { opacity: 1; transform: scale(1); } }
        @media (max-width: 575px) {
          .pp-hero { flex-direction: column; text-align: center; padding: 24px; }
          .pp-chips { justify-content: center; }
        }
        @media (prefers-reduced-motion: reduce) {
          .pp-page, .pp-hero, .pp-avatar, .pp-item, .pp-item .pp-ico, .pp-note,
          .pp-hero-art, .pp-hero-art * { animation: none !important; }
        }
      `}</style>

      {/* Hero */}
      <div className="pp-hero">
        <div className="pp-avatar">
          {showImg ? (
            <img src={getImageUrl(img)} alt={name} onError={() => setImgError(true)} />
          ) : (
            <span>{initials}</span>
          )}
        </div>
        <div className="pp-hero-info">
          <div className="pp-eyebrow">My Profile</div>
          <h2>{name}</h2>
          <div className="pp-email"><i className="bx bx-envelope" />{profile.email || '-'}</div>
          <div className="pp-chips">
            <span className="pp-status"><span className="dot" />{isActive ? 'Active' : 'Pending Approval'}</span>
            {profile.unique_id && <span className="pp-chip"><i className="bx bx-id-card" />{profile.unique_id}</span>}
            {/* <span className="pp-chip"><i className="bx bx-shield-quarter" />Role {profile.role_id || '-'}</span> */}
          </div>
        </div>

        {/* Animated scene: a girl reading a book — same as dashboard */}
        <svg className="pp-hero-art" viewBox="0 0 260 180" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <circle cx="118" cy="92" r="78" fill="rgba(255,255,255,.07)" />
          <circle cx="118" cy="92" r="56" fill="rgba(255,255,255,.06)" />

          <g className="art-shelf">
            <rect x="170" y="26" width="78" height="104" rx="9" fill="rgba(255,255,255,.12)" />
            <line x1="170" y1="62" x2="248" y2="62" stroke="rgba(255,255,255,.22)" strokeWidth="2" />
            <line x1="170" y1="98" x2="248" y2="98" stroke="rgba(255,255,255,.22)" strokeWidth="2" />
            <rect x="178" y="36" width="7" height="24" rx="2" fill="#eef4ff" />
            <rect x="187" y="32" width="7" height="28" rx="2" fill="#6366f1" />
            <rect x="196" y="38" width="7" height="22" rx="2" fill="#3b82f6" />
            <rect x="206" y="34" width="7" height="26" rx="2" fill="#3b82f6" />
            <rect x="216" y="32" width="7" height="28" rx="2" fill="#1e3a8a" />
            <rect x="226" y="36" width="7" height="24" rx="2" fill="#93c5fd" />
            <rect x="178" y="72" width="7" height="24" rx="2" fill="#3b82f6" />
            <rect x="187" y="68" width="7" height="28" rx="2" fill="#3b82f6" />
            <rect x="196" y="74" width="7" height="22" rx="2" fill="#eef4ff" />
            <rect x="207" y="70" width="7" height="26" rx="2" fill="#6366f1" />
            <rect x="217" y="72" width="7" height="24" rx="2" fill="#93c5fd" />
            <rect x="227" y="68" width="7" height="28" rx="2" fill="#1e3a8a" />
          </g>

          <g className="art-plant">
            <path d="M150 150 q4 -26 0 -34" stroke="#3b82f6" strokeWidth="2.5" fill="none" />
            <ellipse cx="150" cy="112" rx="6" ry="9" fill="#3b82f6" transform="rotate(-22 150 112)" />
            <ellipse cx="158" cy="118" rx="6" ry="9" fill="#93c5fd" transform="rotate(20 158 118)" />
            <ellipse cx="143" cy="120" rx="5" ry="8" fill="#93c5fd" transform="rotate(-40 143 120)" />
            <path d="M143 150 L157 150 L154 164 L146 164 Z" fill="#6366f1" />
          </g>

          <g className="art-book-float">
            <rect x="120" y="16" width="36" height="25" rx="4" fill="#eef4ff" />
            <rect x="120" y="16" width="36" height="25" rx="4" fill="none" stroke="#6366f1" strokeWidth="1.5" opacity=".5" />
            <line x1="138" y1="18" x2="138" y2="39" stroke="#6366f1" strokeWidth="2" />
            <line x1="125" y1="24" x2="134" y2="24" stroke="#6366f1" strokeWidth="1.5" opacity=".7" />
            <line x1="142" y1="24" x2="151" y2="24" stroke="#6366f1" strokeWidth="1.5" opacity=".7" />
            <line x1="125" y1="30" x2="134" y2="30" stroke="#6366f1" strokeWidth="1.5" opacity=".5" />
            <line x1="142" y1="30" x2="151" y2="30" stroke="#6366f1" strokeWidth="1.5" opacity=".5" />
          </g>

          <g fill="#fde047">
            <path className="art-twinkle t1" transform="translate(108 30)" d="M0 -4 L1.1 -1.1 L4 0 L1.1 1.1 L0 4 L-1.1 1.1 L-4 0 L-1.1 -1.1 Z" />
            <path className="art-twinkle t2" transform="translate(166 16)" d="M0 -3 L0.9 -0.9 L3 0 L0.9 0.9 L0 3 L-0.9 0.9 L-3 0 L-0.9 -0.9 Z" />
            <path className="art-twinkle t3" transform="translate(96 56)" d="M0 -3 L0.9 -0.9 L3 0 L0.9 0.9 L0 3 L-0.9 0.9 L-3 0 L-0.9 -0.9 Z" />
          </g>

          <g className="art-girl">
            <ellipse cx="86" cy="158" rx="56" ry="10" fill="rgba(255,255,255,.16)" />
            <ellipse cx="86" cy="153" rx="44" ry="11" fill="#3b82f6" />
            <path d="M52 150 Q86 138 120 150 Q114 162 86 162 Q58 162 52 150 Z" fill="#1e3a8a" />
            <path d="M70 150 Q86 144 102 150" stroke="#172554" strokeWidth="2" fill="none" />
            <path d="M62 138 Q60 110 86 108 Q112 110 110 138 Z" fill="#6366f1" />
            <path d="M62 138 Q60 110 86 108 Q112 110 110 138 Z" fill="none" stroke="#4f46e5" strokeWidth="1" opacity=".4" />
            <path d="M66 120 Q58 130 64 140" stroke="#6366f1" strokeWidth="9" fill="none" strokeLinecap="round" />
            <path d="M106 120 Q114 130 108 140" stroke="#6366f1" strokeWidth="9" fill="none" strokeLinecap="round" />
            <circle cx="64" cy="141" r="4.5" fill="#fcd9b8" />
            <circle cx="108" cy="141" r="4.5" fill="#fcd9b8" />
            <g className="art-head">
              <path d="M68 90 Q66 70 86 70 Q106 70 104 90 L104 116 Q98 110 96 100 L96 92 Q96 82 86 82 Q76 82 76 92 L76 100 Q74 110 68 116 Z" fill="#4f4a35" />
              <rect x="82" y="98" width="8" height="10" rx="3" fill="#fcd9b8" />
              <circle cx="86" cy="90" r="13" fill="#fcd9b8" />
              <circle cx="79" cy="93" r="2.4" fill="#cbb8a0" opacity=".7" />
              <circle cx="93" cy="93" r="2.4" fill="#cbb8a0" opacity=".7" />
              <path d="M80 89 q2.5 2.5 5 0" stroke="#3b2a20" strokeWidth="1.8" fill="none" strokeLinecap="round" />
              <path d="M88 89 q2.5 2.5 5 0" stroke="#3b2a20" strokeWidth="1.8" fill="none" strokeLinecap="round" />
              <path d="M83 96 q3 2.5 6 0" stroke="#6366f1" strokeWidth="1.6" fill="none" strokeLinecap="round" />
              <path d="M73 88 Q74 72 86 72 Q98 72 99 88 Q95 80 86 80 Q77 80 73 88 Z" fill="#4f4a35" />
              <circle cx="100" cy="78" r="6" fill="#4f4a35" />
              <circle cx="72" cy="78" r="6" fill="#4f4a35" />
            </g>
            <g className="art-openbook">
              <path d="M56 134 L86 126 L86 150 L56 156 Z" fill="#f1f6ff" />
              <path d="M116 134 L86 126 L86 150 L116 156 Z" fill="#ffffff" />
              <path d="M86 126 L86 150" stroke="#bfdbfe" strokeWidth="1.6" />
              <path className="art-page" d="M86 126 L114 133 L114 149 L86 150 Z" fill="#dbeafe" />
              <line x1="62" y1="138" x2="80" y2="133" stroke="#bfdbfe" strokeWidth="1.4" />
              <line x1="62" y1="143" x2="80" y2="138" stroke="#bfdbfe" strokeWidth="1.4" />
              <line x1="92" y1="133" x2="110" y2="138" stroke="#bfdbfe" strokeWidth="1.4" />
              <line x1="92" y1="138" x2="110" y2="143" stroke="#bfdbfe" strokeWidth="1.4" />
            </g>
          </g>
        </svg>
      </div>

      {/* Section heading */}
      <div className="pp-section">
        <i className="bx bx-detail" />
        <h4>Account Details</h4>
      </div>

      {/* Detail cards */}
      <div className="pp-grid">
        {details.map((d, i) => (
          <div key={d.label} className={`pp-item pp-d${i + 1}`} style={{ '--c': d.c }}>
            <div className="pp-ico"><i className={d.icon} /></div>
            <div>
              <div className="pp-label">{d.label}</div>
              <div className="pp-value">{d.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* <div className="pp-note">
        <i className="bx bx-info-circle fs-5" />
        Profile update abhi admin/librarian approval ke through manage hoga.
      </div> */}
    </div>
  );
};

export default ParentProfile;